/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InlineSVGConfig } from './inline-svg.config';
import { InlineSVGService } from './inline-svg.service';
import { SpriteIconPath } from './sprite-icon-names.const';
import { SVGCacheService } from './svg-cache.service';
import * as SvgUtil from './svg-util';

@Directive({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: '[inlineSVG]',
  providers: [InlineSVGService, SVGCacheService],
})
export class InlineSVGDirective implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private _el = inject(ElementRef);
  private _renderer = inject(Renderer2);
  private _inlineSVGService = inject(InlineSVGService);
  private _svgCache = inject(SVGCacheService);
  private _config = inject(InlineSVGConfig, { optional: true })!;

  private readonly _supportsSVG: boolean;
  private _prevUrl!: string;
  readonly inlineSVG = input.required<SpriteIconPath | string>();
  readonly resolveSVGUrl = input(true);
  readonly replaceContents = input(true);
  readonly prepend = input(false);
  readonly cacheSVG = input(true);
  readonly setSVGAttributes = input<{
    [key: string]: any;
  }>({});
  readonly removeSVGAttributes = input<string[]>([]);
  readonly forceEvalStyles = input(false);
  readonly fallbackImgUrl = input<string | undefined>();
  readonly SVGLoaded = input<(svg: SVGElement, parent: Element | null) => SVGElement>();
  readonly SVGInserted = output<SVGElement>();
  readonly SVGFailed = output<any>();
  _prevSVG!: SVGElement;

  constructor() {
    this._supportsSVG = SvgUtil.isSvgSupported();

    if (!this._supportsSVG) {
      this._fail('Embed SVG are not supported by this browser');
    }
  }

  ngOnInit(): void {
    this._insertSVG();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const setSVGAttributesChanged = Boolean(changes['setSVGAttributes']);
    if (changes['inlineSVG'] || setSVGAttributesChanged) {
      this._insertSVG(setSVGAttributesChanged);
    }
  }

  private _insertSVG(force = false): void {
    if (!this._supportsSVG) {
      return;
    }

    const inlineSVG = this.inlineSVG();
    if (!inlineSVG) {
      this._fail('No URL passed to [inlineSVG]');
      return;
    }

    if (!force && inlineSVG === this._prevUrl) {
      return;
    }
    this._prevUrl = inlineSVG;

    this._svgCache
      .getSVG(inlineSVG, this.resolveSVGUrl(), this.cacheSVG())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (svg: SVGElement) => {
          const inlineSVGValue = this.inlineSVG();
          if (SvgUtil.isUrlSymbol(inlineSVGValue)) {
            const symbolId = inlineSVGValue.split('#')[1];
            svg = SvgUtil.createSymbolSvg(this._renderer, svg, symbolId);
          }

          this._processSvg(svg);
        },
        error: (err: any) => {
          this._fail(err);
        },
      });
  }

  private _processSvg(svg: SVGElement) {
    if (!svg) {
      return;
    }

    const removeSVGAttributes = this.removeSVGAttributes();
    if (removeSVGAttributes) {
      SvgUtil.removeAttributes(svg, removeSVGAttributes);
    }

    const setSVGAttributes = this.setSVGAttributes();
    if (setSVGAttributes) {
      SvgUtil.setAttributes(svg, setSVGAttributes);
    }

    const SVGLoaded = this.SVGLoaded();
    if (SVGLoaded) {
      svg = SVGLoaded(svg, this._el.nativeElement);
    }

    this._insertEl(svg);

    if (this.forceEvalStyles()) {
      const styleTags = svg.querySelectorAll('style');
      Array.from(styleTags).forEach(tag => (tag.textContent += ''));
    }

    this.SVGInserted.emit(svg);
  }

  private _insertEl(el: HTMLElement | SVGElement): void {
    this._inlineSVGService.insertEl(this, this._el.nativeElement, el, this.replaceContents(), this.prepend());
  }

  private _fail(msg: string): void {
    this.SVGFailed.emit(msg);

    const fallbackImgUrl = this.fallbackImgUrl();
    if (fallbackImgUrl) {
      const elImg = this._renderer.createElement('IMG');
      this._renderer.setAttribute(elImg, 'src', fallbackImgUrl);

      this._insertEl(elImg);
    }
  }
}
